/**
 * 收银台监听工具 - 主入口
 * @description 监听收银台关键元素的渲染状态
 * @author Nemo
 * @date 2026-01-26
 */

import {
  OrderObserver,
  ElementObserver,
  SubmitButtonObserver,
} from './observers';
import { reportError } from './helpers';

/**
 * 收银台监听器
 * @example
 * const monitor = new CheckoutMonitor(this);
 * monitor.start({
 *   order: true,
 *   googlePay: true,
 *   applePay: true,
 *   paymentForm: true,
 *   submitButton: true,
 * });
 */
export class CheckoutMonitor {
  constructor(vm) {
    this.vm = vm; // Vue实例
    this.observers = new Map(); // 存储所有观察器
    this.results = new Map(); // 存储监听结果
    this.startTime = null; // 启动时间戳
  }

  /**
   * 启动监听
   * @param {Object} config - 监听配置
   */
  start(config = {}) {
    // 记录启动时间
    this.startTime = Date.now();

    // 清理旧的观察器
    this.destroy();

    // 创建并启动观察器
    if (config.order) {
      this.addObserver('order', new OrderObserver({
        vm: this.vm,
        onReady: (data, success) => this.handleReady('order', data, success),
      }));
    }

    if (config.googlePay) {
      this.addObserver('googlePay', new ElementObserver({
        name: 'GooglePay按钮',
        vm: this.vm,
        selector: '.google-pay-button',
        onReady: (element, success) => this.handleReady('googlePay', element, success),
      }));
    }

    if (config.applePay) {
      this.addObserver('applePay', new ElementObserver({
        name: 'ApplePay按钮',
        vm: this.vm,
        selector: '.apple-pay-btn',
        onReady: (element, success) => this.handleReady('applePay', element, success),
      }));
    }

    if (config.paymentForm) {
      this.addObserver('paymentForm', new ElementObserver({
        name: '支付表单',
        vm: this.vm,
        selector: '._payment-wrapper, .payment-wrapper',  // 兼容PC端和移动端
        onReady: (element, success) => this.handleReady('paymentForm', element, success),
      }));
    }

    if (config.submitButton) {
      this.addObserver('submitButton', new SubmitButtonObserver({
        vm: this.vm,
        onReady: (element, success) => this.handleReady('submitButton', element, success),
      }));
    }

    // 启动所有观察器
    this.observers.forEach(observer => observer.start());
  }

  /**
   * 添加观察器
   */
  addObserver(name, observer) {
    this.observers.set(name, observer);
  }

  /**
   * 处理元素渲染结果
   * @param {String} name - 元素名称
   * @param {*} data - 元素数据
   * @param {Boolean} success - 是否成功
   */
  handleReady(name, data, success) {
    try {
      const timestamp = Date.now();
      const duration = this.startTime ? timestamp - this.startTime : 0;

      this.results.set(name, {
        ready: success,
        data,
        timestamp,
        duration,
      });

      // 元素类型映射
      const elementTypeMap = {
        order: 'order',
        googlePay: 'google_pay_button',
        applePay: 'apple_pay_button',
        paymentForm: 'payment_form',
        submitButton: 'submit_button',
      };

      // 发送埋点（成功和失败都上报）
      if (this.vm && typeof this.vm.sendRiskDeviceFingerPrint === 'function') {
        try {
          this.vm.sendRiskDeviceFingerPrint({
            e: 'element_render',
            lmo: JSON.stringify({
              element_type: elementTypeMap[name] || name,
              duration,
              success, // true / false
            }),
          });
        } catch (error) {
          // 埋点发送失败，上报错误
          reportError(this.vm, error, 'send_element_render');
        }
      }

      // 触发Vue事件（只在成功时触发）
      if (success && this.vm && typeof this.vm.$emit === 'function') {
        this.vm.$emit('checkout-element-ready', {
          name,
          data,
        });
      }
    } catch (error) {
      // 捕获所有异常，上报错误
      reportError(this.vm, error, 'handle_ready');
    }
  }

  /**
   * 获取监听结果
   */
  getResults() {
    return Object.fromEntries(this.results);
  }

  /**
   * 检查所有元素是否就绪
   */
  isAllReady() {
    return this.observers.size === this.results.size;
  }

  /**
   * 清理所有观察器
   */
  destroy() {
    try {
      if (this.observers && this.observers.size > 0) {
        this.observers.forEach(observer => {
          if (observer && typeof observer.stop === 'function') {
            observer.stop();
          }
        });
        this.observers.clear();
      }

      if (this.results) {
        this.results.clear();
      }
    } catch (error) {
      // 销毁失败，上报错误
      reportError(this.vm, error, 'destroy');
    }
  }
}

// 导出单例工厂函数（可选）
export function createCheckoutMonitor(vm) {
  return new CheckoutMonitor(vm);
}
